import classNames from '@/utils/classnames';

export interface TabItem<T extends string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
}

interface TabBarProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  tabs: TabItem<T>[];
  className?: string;
  ariaLabel?: string;
}

/**
 * Pill-style tab bar (active tab lifts to surface-3). For a bordered,
 * single-select segmented look use {@link SegmentedControl} instead.
 */
export function TabBar<T extends string>({ value, onChange, tabs, className, ariaLabel }: TabBarProps<T>) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={classNames('flex items-center gap-1', className)}>
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={classNames(
              'flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-xs font-medium outline-none transition-colors',
              active ? 'bg-surface-3 text-fg' : 'text-fg-tertiary hover:bg-surface-2 hover:text-fg-secondary',
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
